/**
 * Service façade pour l'accès aux données de configuration
 * Gère l'état de la configuration courante et fournit une interface typée
 */

import type {
  GradeItem,
  GradeListConfig,
  ItemConfig,
  MaterialConfig,
  FeatureConfig,
  CombinationConfig
} from '../types';

import { ConfigLoader, ConfigLoaderError, type GradeConfigData } from './ConfigLoader';

/**
 * Erreurs spécifiques au DataService
 */
export class DataServiceError extends Error {
  constructor(
    message: string,
    public readonly type: 'no_current_grade' | 'grade_not_found' | 'load_failed',
    public readonly gradeId?: string
  ) {
    super(message);
    this.name = 'DataServiceError';
  }
}

/**
 * État interne du DataService
 */
interface DataServiceState {
  currentGrade: GradeItem | null;
  gradeList: GradeListConfig | null;
  gradeData: GradeConfigData | null;
  isLoaded: boolean;
}

/**
 * Service DataService - Façade pour l'accès aux données de configuration
 */
export class DataService {
  private static instance: DataService;
  private state: DataServiceState;
  private configLoader: ConfigLoader;

  private constructor() {
    this.configLoader = ConfigLoader.getInstance();
    this.state = {
      currentGrade: null,
      gradeList: null,
      gradeData: null,
      isLoaded: false
    };
  }

  /**
   * Obtient l'instance singleton du DataService
   */
  public static getInstance(): DataService {
    if (!DataService.instance) {
      DataService.instance = new DataService();
    }
    return DataService.instance;
  }

  /**
   * Définit le grade courant et charge ses données
   */
  public async setCurrentGrade(gradeId: string): Promise<void> {
    try {
      // Charger la liste des grades si pas encore fait
      if (!this.state.gradeList) {
        this.state.gradeList = await this.configLoader.loadGradeList();
      }

      // Trouver le grade demandé
      const grade = this.findGradeById(gradeId);
      if (!grade) {
        throw new DataServiceError(
          `Grade avec l'ID "${gradeId}" non trouvé`,
          'grade_not_found',
          gradeId
        );
      }

      // Charger les données du grade
      const gradeData = await this.configLoader.loadGradeConfig(gradeId);

      // Mettre à jour l'état
      this.state.currentGrade = grade;
      this.state.gradeData = gradeData;
      this.state.isLoaded = true;

    } catch (error) {
      if (error instanceof ConfigLoaderError) {
        throw new DataServiceError(
          `Échec du chargement du grade "${gradeId}": ${error.message}`,
          'load_failed',
          gradeId
        );
      }
      throw error;
    }
  }

  /**
   * Obtient le grade courant
   */
  public getCurrentGrade(): GradeItem | null {
    return this.state.currentGrade;
  }

  /**
   * Obtient la liste de tous les grades disponibles
   */
  public async getAvailableGrades(): Promise<GradeItem[]> {
    if (!this.state.gradeList) {
      this.state.gradeList = await this.configLoader.loadGradeList();
    }

    const grades: GradeItem[] = [];
    for (const group of this.state.gradeList.GRADE.groups) {
      grades.push(...group.items);
    }

    return grades;
  }

  /**
   * Obtient la configuration des items du grade courant
   */
  public getItems(): ItemConfig {
    this.ensureGradeLoaded();
    return this.state.gradeData!.items;
  }

  /**
   * Obtient la configuration des matériaux du grade courant
   */
  public getMaterials(): MaterialConfig {
    this.ensureGradeLoaded();
    return this.state.gradeData!.materials;
  }

  /**
   * Obtient la configuration des features du grade courant
   */
  public getFeatures(): FeatureConfig {
    this.ensureGradeLoaded();
    return this.state.gradeData!.features;
  }

  /**
   * Obtient la configuration des combinaisons du grade courant
   */
  public getCombinations(): CombinationConfig {
    this.ensureGradeLoaded();
    return this.state.gradeData!.combinations;
  }

  /**
   * Vérifie si un grade est chargé
   */
  public isGradeLoaded(): boolean {
    return this.state.isLoaded;
  }

  /**
   * Réinitialise l'état du service
   */
  public reset(): void {
    this.state = {
      currentGrade: null,
      gradeList: null,
      gradeData: null,
      isLoaded: false
    };
    this.configLoader.clearCache();
  }

  /**
   * Obtient l'ID du grade courant
   */
  public getCurrentGradeId(): string | null {
    return this.state.currentGrade?.item_id || null;
  }

  /**
   * Trouve un grade par son ID
   */
  private findGradeById(gradeId: string): GradeItem | null {
    if (!this.state.gradeList) {
      return null;
    }

    for (const group of this.state.gradeList.GRADE.groups) {
      const grade = group.items.find(item => item.item_id === gradeId);
      if (grade) {
        return grade;
      }
    }
    return null;
  }

  /**
   * S'assure qu'un grade est chargé
   */
  private ensureGradeLoaded(): void {
    if (!this.state.isLoaded || !this.state.gradeData) {
      throw new DataServiceError(
        'Aucun grade n\'est actuellement chargé',
        'no_current_grade'
      );
    }
  }
}

// Export de l'instance singleton par défaut
export const dataService = DataService.getInstance();